import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import classnames from 'classnames'

// Selectors
import { selectCustomerFirstName } from 'src/store/selectors/customerProfile'
import {
  selectInstantOfferVideoId,
  selectInstantOfferVideoType,
} from 'src/store/selectors/videos'
import { selectHomeFactsProgress } from 'src/store/selectors/valuation'
import { selectHasCMA } from 'src/store/selectors/home/cma'

// Actions
import { trackInstantOfferRequested } from 'src/actions/home'
import { setValuationHomeFactsProgress } from 'src/actions/valuation'

// Components
import Text from 'src/Components/shared/Text'
import Button from 'src/Components/shared/Button'
import { If, Unless, Page } from 'src/Components'
import { Player } from 'src/Components/shared/Player'
import ErrorLogoWithMessage from 'src/Components/shared/Error/LogoWithMessage'
import HomeFacts from 'src/Components/homeowners/Valuation/HomeFacts/HomeFacts'
import { SlideOnMount, FadeOnMount } from 'src/Components/shared/Animations'
import panelStyles from 'src/Components/shared/Panel/Panel.module.scss'
import InstantOfferConfirmationPanel from './InstantOfferConfirmationPanel'

// Styles
import styles from './InstantOffer.module.scss'

const InstantOffer = () => {
  const [success, setSuccess] = useState(false)
  const dispatch = useDispatch()

  const customerFirstName = useSelector(selectCustomerFirstName)
  const videoId = useSelector(selectInstantOfferVideoId)
  const videoType = useSelector(selectInstantOfferVideoType)

  const hasCma = useSelector(selectHasCMA)
  const progress = useSelector(selectHomeFactsProgress)
  const handleClick = () => {
    dispatch(trackInstantOfferRequested())

    if (!hasCma) {
      // Go to Home Facts
      dispatch(setValuationHomeFactsProgress(20))
    } else {
      setSuccess(true)
    }
  }

  return (
    <Page>
      <Page.Body className={styles.body}>
        <Unless condition={success}>
          <Unless condition={progress}>
            <SlideOnMount
              className={classnames(panelStyles.headWrap, styles.head)}
            >
              <Text
                size='xl'
                align='center'
                variant='neutralCool'
                variantCount='900'
              >
                <FormattedMessage
                  id='Homeowner.instantOffer.header'
                  defaultMessage='What is an instant offer?'
                />
              </Text>
              <Text variant='neutralCool' variantCount='700' size='s'>
                <FormattedMessage
                  id='Homeowner.instantOffer.body'
                  defaultMessage='An instant offer could be an alternative way to sell your home or gauge its current market value. Work with {name} to better understand instant offers!'
                  values={{
                    name: customerFirstName,
                  }}
                />
              </Text>
            </SlideOnMount>
            <SlideOnMount className={panelStyles.contentWrap}>
              <If condition={videoType && videoId}>
                <Player
                  videoType={videoType}
                  videoId={videoId}
                  className={styles.videoPlayer}
                />
              </If>
              <Unless condition={videoType && videoId}>
                <ErrorLogoWithMessage>
                  <FormattedMessage
                    id='Homeowner.instantOffer.player.error'
                    defaultMessage='Unable to recognize player settings. Cannot render video.'
                  />
                </ErrorLogoWithMessage>
              </Unless>
            </SlideOnMount>
            <FadeOnMount className={panelStyles.submitWrap}>
              <Button primary fullWidth onClick={handleClick}>
                <FormattedMessage
                  id='Homeowner.instantOffer.moreInfo'
                  defaultMessage='Get more info'
                />
              </Button>
            </FadeOnMount>
          </Unless>
          <If condition={progress && progress !== 100}>
            <HomeFacts />
          </If>
        </Unless>
        <If condition={success || progress === 100}>
          <InstantOfferConfirmationPanel />
        </If>
      </Page.Body>
    </Page>
  )
}

export default InstantOffer
